import { memo, useEffect, useState } from "react";
import { ErrorOutline, InfoOutlined } from "@mui/icons-material";
import { Box, Modal } from "@mui/material";
import { ButtonComponent } from "@zenith/components";

export interface UndoSaleModalProps {
  isOpen: boolean;
  /** A static account sale was paid into the account, so its money must be returned by hand. */
  isPaidIntoAccount: boolean;
  onUndo: () => void;
  onClose: () => void;
}

const UndoSaleModal = memo(function UndoSaleModal({
  isOpen,
  isPaidIntoAccount,
  onUndo,
  onClose,
}: UndoSaleModalProps) {
  const [isFundsNoteAcknowledged, setIsFundsNoteAcknowledged] =
    useState<boolean>(false);

  useEffect(() => {
    if (!isOpen) setIsFundsNoteAcknowledged(false);
  }, [isOpen]);

  const handleUndo = () => {
    if (isPaidIntoAccount && !isFundsNoteAcknowledged) {
      setIsFundsNoteAcknowledged(true);
      return;
    }
    onUndo();
  };

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      aria-labelledby="undo-sale-title"
      className="overflow-visible"
    >
      <Box className="absolute top-1/2 left-1/2 flex w-full -translate-x-1/2 -translate-y-1/2 transform flex-col items-center rounded-xl bg-white px-6 py-8 outline-none sm:w-3/4 md:w-1/2 lg:w-[40%] xl:w-100">
        <ErrorOutline className="text-neutral-900" sx={{ fontSize: 32 }} />
        <h2
          id="undo-sale-title"
          className="mt-3 text-xl font-medium text-neutral-900"
        >
          Undo Sales
        </h2>
        <p className="mt-2 text-center text-sm text-neutral-500">
          Undoing this sale will reverse this action and the stock will be added
          back to your inventory
        </p>

        {isPaidIntoAccount && !isFundsNoteAcknowledged && (
          <div
            id="undo-sale-funds-note"
            className="mt-4 flex w-full gap-2 rounded-lg bg-indigo-50 px-4 py-3 text-xs text-neutral-700"
          >
            <InfoOutlined className="text-indigo-500" sx={{ fontSize: 16 }} />
            <span>
              Please note that only the stock in the inventory would be
              reversed, you would have to initiate a manual reversal of funds
            </span>
          </div>
        )}

        <div className="mt-8 flex w-full gap-4">
          <ButtonComponent
            id="confirm-undo-sale-button"
            label="Undo Sales"
            onClick={handleUndo}
            fullWidth
            size="large"
            type="secondary"
          />
          <ButtonComponent
            id="cancel-undo-sale-button"
            label="Go Back"
            onClick={onClose}
            fullWidth
            size="large"
            type="primary"
          />
        </div>
      </Box>
    </Modal>
  );
});

export default UndoSaleModal;
